import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load your page components.
const HomePage = lazy(() => import('./HomePage'));
const Login = lazy(() => import('./components/common/Login'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const SignUp = lazy(() => import('./components/common/Signup'));
const ForgotPassword = lazy(() => import('./components/common/ForgotPassword'));
const PlayerProps = lazy(() => import('./components/common/PlayerProps'));
const ResearchArticle = lazy(() => import('./components/research/ResearchArticle'));
const ResearchLinksMessages = lazy(() => import('./components/research/ResearchLinksMessages'));

// Lazy-load your new NBA Player Props Dashboard
const NbaPlayerPropsDashboard = lazy(() =>
  import('./components/common/NBA/NbaPlayerPropsDashboard')
);

function App() {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Aether Sports",
    "url": "https://aether-sports.web.app/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://aether-sports.web.app/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
          <Routes>
            {/* 1) Redirect root ("/") to "/dashboard". */}
            <Route path="/" element={<Navigate to="/dashboard" />} />

            {/* 2) Old Home Page */}
            <Route
              path="/home"
              element={
                <>
                  <Helmet>
                    <title>Aether Sports – Revolutionizing Sports Betting</title>
                    <meta
                      name="description"
                      content="At Aether Sports, we harness advanced technologies like LLM, MLM, and our proprietary QAgents to transform raw data into smarter bets. Elevate your game with data-driven insights, bet tracking, and advanced analytics for NBA, MLB, and more."
                    />
                    <meta
                      name="keywords"
                      content="sports betting, NBA, MLB, AI predictions, data analytics, advanced technology, Aether Sports"
                    />
                    <link rel="canonical" href="https://aether-sports.web.app/home" />
                    <script type="application/ld+json">
                      {JSON.stringify(schemaMarkup)}
                    </script>
                  </Helmet>
                  <HomePage />
                </>
              }
            />

            {/* 3) Main Dashboard */}
            <Route
              path="/dashboard"
              element={
                <>
                  <Helmet>
                    <title>Your Sports Betting Dashboard | Aether Sports</title>
                    <meta
                      name="description"
                      content="View your current bets and leverage advanced analytics and AI predictions on your personalized dashboard—covering NBA, MLB, and other sports."
                    />
                    <meta
                      name="keywords"
                      content="sports betting, dashboard, NBA, MLB, AI predictions, Aether Sports"
                    />
                    <link rel="canonical" href="https://aether-sports.web.app/dashboard" />
                    <script type="application/ld+json">
                      {JSON.stringify(schemaMarkup)}
                    </script>
                  </Helmet>
                  <Dashboard />
                </>
              }
            />

            {/* 4) Authentication */}
            <Route
              path="/login"
              element={
                <>
                  <Helmet>
                    <title>Login to Aether Sports</title>
                    <meta
                      name="description"
                      content="Login to access your sports betting dashboard and track advanced analytics for smarter bets covering NBA, MLB, and beyond."
                    />
                    <link rel="canonical" href="https://aether-sports.web.app/login" />
                    <script type="application/ld+json">
                      {JSON.stringify(schemaMarkup)}
                    </script>
                  </Helmet>
                  <Login />
                </>
              }
            />
            <Route
              path="/signup"
              element={
                <>
                  <Helmet>
                    <title>Sign Up for Aether Sports</title>
                    <meta
                      name="description"
                      content="Join Aether Sports to access revolutionary sports betting tools and AI-driven predictions. Start your free 3-day trial today!"
                    />
                    <link rel="canonical" href="https://aether-sports.web.app/signup" />
                    <script type="application/ld+json">
                      {JSON.stringify(schemaMarkup)}
                    </script>
                  </Helmet>
                  <SignUp />
                </>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <>
                  <Helmet>
                    <title>Forgot Password – Aether Sports</title>
                    <meta
                      name="description"
                      content="Recover your password to regain access to your sports betting account and continue leveraging advanced data insights for NBA, MLB, and more."
                    />
                    <link rel="canonical" href="https://aether-sports.web.app/forgot-password" />
                    <script type="application/ld+json">
                      {JSON.stringify(schemaMarkup)}
                    </script>
                  </Helmet>
                  <ForgotPassword />
                </>
              }
            />

            {/* 5) NBA Player Props Dashboard */}
            <Route
              path="/sports-betting/nba/player-props"
              element={
                <>
                  <Helmet>
                    <title>NBA Player Props – AI Predictions & Betting Insights | Aether Sports</title>
                    <meta
                      name="description"
                      content="Explore NBA player props with AI-driven projections and predictive models. Aether Sports helps you bet smarter using real-time data, machine learning, and expert-level analysis."
                    />
                    <meta
                      name="keywords"
                      content="NBA player props, sports betting, AI projections, predictive analytics, machine learning, Aether Sports"
                    />
                    <link rel="canonical" href="https://aether-sports.web.app/sports-betting/nba/player-props" />
                    <meta property="og:title" content="NBA Player Props – AI Predictions & Betting Insights | Aether Sports" />
                    <meta property="og:description" content="Explore NBA player props with AI-driven projections and predictive models. Aether Sports helps you bet smarter using real-time data, machine learning, and expert-level analysis." />
                    <meta property="og:type" content="website" />
                    <meta property="og:url" content="https://aether-sports.web.app/sports-betting/nba/player-props" />
                    <meta property="og:image" content="https://aether-sports.web.app/logo.png" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="NBA Player Props – AI Predictions & Betting Insights | Aether Sports" />
                    <meta name="twitter:description" content="Explore NBA player props with AI-driven projections and predictive models. Aether Sports helps you bet smarter using real-time data, machine learning, and expert-level analysis." />
                    <meta name="twitter:image" content="https://aether-sports.web.app/logo.png" />
                    <script type="application/ld+json">
                      {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "NBA Player Props – AI Predictions",
                        "url": "https://aether-sports.web.app/sports-betting/nba/player-props",
                        "description": "Explore NBA player props with AI-driven projections and predictive models. Aether Sports helps you bet smarter using real-time data, machine learning, and expert-level analysis.",
                        "publisher": {
                          "@type": "Organization",
                          "name": "Aether Sports",
                          "url": "https://aether-sports.web.app"
                        }
                      })}
                    </script>
                  </Helmet>
                  <NbaPlayerPropsDashboard />
                </>
              }
            />

            {/* 6) Legacy Player Props */}
            <Route
              path="/player-props"
              element={
                <>
                  <Helmet>
                    <title>Player Props Predictions | Aether Sports</title>
                    <meta
                      name="description"
                      content="Unlock advanced player props predictions for NBA, MLB, and other sports with data-driven insights and cutting-edge AI technology."
                    />
                    <link rel="canonical" href="https://aether-sports.web.app/player-props" />
                    <script type="application/ld+json">
                      {JSON.stringify(schemaMarkup)}
                    </script>
                  </Helmet>
                  <PlayerProps />
                </>
              }
            />

            {/* 7) Research */}
            <Route path="/research/:slug" element={<ResearchArticle />} />
            <Route path="/research" element={<ResearchLinksMessages />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
